import React, { PropsWithChildren, useState } from 'react';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TenancyContextProvider } from '@/contexts/TenancyContextProvider';
import { UsersListProvider } from '@/contexts/UsersListProvider';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AuditorSidebar } from './AuditorSidebar';
import { AuditorSidebarConfig, DEFAULT_AUDITOR_CONFIG } from './auditor-sidebar-config';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

interface AuditorLayoutProps {
  config?: AuditorSidebarConfig;
  tenancyName?: string;
}

export function AuditorLayout({ 
  children, 
  config = DEFAULT_AUDITOR_CONFIG,
  tenancyName = 'Auditor View' 
}: PropsWithChildren<AuditorLayoutProps>) {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Auth gating
  function GatedContent() {
    const { isAuthReady, isAuthenticated } = useAuth();
    if (!isAuthReady) return <div>Loading authentication...</div>;
    if (!isAuthenticated) return <div>Please log in to access the Auditor View.</div>;
    
    // Debug: Log when GatedContent renders
    console.log('GatedContent rendering with children:', { 
      hasChildren: !!children,
      childrenType: children ? typeof children : 'none',
      isAuthReady,
      isAuthenticated
    });
    
    return (
      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <div 
          className="group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full"
          style={{
            '--sidebar-width': '16rem',
            '--sidebar-width-icon': '3rem'
          } as React.CSSProperties}
        >
          {/* Auditor Sidebar */}
          <AuditorSidebar config={config} />
          {/* Main Content */}
          <main className="flex-1 overflow-auto">
            <div className="flex flex-col">
              {/* Simple Header */}
              <div className="h-14 border-b border-border flex items-center p-4">
  <h2 className="text-xl font-semibold">{tenancyName}</h2>
</div>
              {/* Page Content */}
              <div className="flex-1 p-4 overflow-auto">
                {children || <div>No content to display</div>}
              </div>
            </div>
          </main>
        </div>
        {/* Toast notifications */}
        <Toaster theme="light" richColors position="bottom-left" closeButton />
      </SidebarProvider>
    );
  }

  // Debug: Log when layout renders and what children it has
  console.log('AuditorLayout rendering with children:', { 
    hasChildren: !!children,
    childrenType: children ? typeof children : 'none'
  });

  return (
    <ErrorBoundary>
      <UsersListProvider>
        <ThemeProvider>
          <AuthProvider>
            <TenancyContextProvider>
              <GatedContent />
            </TenancyContextProvider>
          </AuthProvider>
        </ThemeProvider>
      </UsersListProvider>
    </ErrorBoundary>
  );
}

export default AuditorLayout;
